import Avatar from "@/components/Avatar";
import Menu, { type MenuItem, type MenuListEntry } from "@/components/Menu";
import { MENU_PANEL_WIDTH_WIDE } from "@/components/Menu/menuLayout";
import NavLogo from "./NavLogo";
import { defaultNavLinks } from "@/layouts/AppLayout/navLinks";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanViewTasksBool,
  useHasCapabilityBool,
  useHasRoles
} from "@/hooks/useCapabilities";
import { Path, useNavigate } from "@/router";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo } from "react";
import NavItem from "../NavItem";
import { useWindowSize } from "@/hooks/useWindowSize";
import { isLargeDesktop } from "@/assets/theme/sizes";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import Badge from "@/components/Badge";
import type { EffectiveDisplayUser } from "@/hooks/useEffectiveDisplayUser";
import { useFlags } from "@/hooks/useFeatureFlags/useFlags";
import { canAccessMcpPlayground } from "@/features/mcp-playground/access";
import useOpenTaskCount from "@/hooks/useOpenTaskCount";
import { usePrimarySection } from "@/hooks/usePrimarySection";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import { getInternalReturnUrl } from "@/providers/AssumeUserProvider/storage";
import { getBackendSourceMenuItems } from "@/lib/backendSource";
import CompactPrimaryNavMenu from "./CompactPrimaryNavMenu";
import type { PrimaryNavTabItem } from "@/layouts/AppLayout/primaryNavTabItems";
import { getGoNavChordKey, primaryNavItemSlug } from "@/layouts/AppLayout/primaryNavTabItems";
import { COMPACT_GLOBAL_NAVBAR_AVATAR_SIZE_CLASS, COMPACT_GLOBAL_NAVBAR_LOGO_SIZE_CLASS } from "./compactNavbarChrome";
import { isDesktopNavLinkActive } from "./desktopNav";
import { useDesktopNavOverflow } from "./useDesktopNavOverflow";
import GoNavSlidingGhost from "./GoNavSlidingGhost";
import classNames from "classnames";
import KeyboardShortcutsDialog from "@/components/KeyboardShortcutsDialog";
import useKeyboardShortcutsHelpShortcut from "@/hooks/useKeyboardShortcutsHelpShortcut";
import type { KeyboardShortcutEntry } from "@/lib/keyboardShortcutCatalog";
import { buildKeyboardShortcutEntries } from "@/lib/keyboardShortcutCatalog";

type TopActionBarProps = {
  interior?: boolean;
  user: EffectiveDisplayUser;
  logout(): void;
  actions?: React.ReactNode;
  hasSecondaryNav?: boolean;
  primaryNav?: {
    tabItems: PrimaryNavTabItem[];
    domains: DOMAIN[];
  };
  primaryNavGoNav?: {
    menuOpen: boolean;
    onMenuOpenChange: (open: boolean) => void;
    goNavActive: boolean;
  };
  keyboardShortcutEntries?: KeyboardShortcutEntry[];
};

function getScrollDirection(setIsScrolled: (isScrolled: boolean) => void) {
  const expandThreshold = 30; // Threshold to expand (when scrolling up)
  const condensedThreshold = 80; // Threshold to condense (when scrolling down)
  let ticking = false;
  let lastState = false;
  let lastScrollY = window.scrollY;
  let scrollDirection: "up" | "down" | null = null;
  let stateChangeCount = 0;
  let lastStateChangeTime = 0;

  const updateScrollState = () => {
    const currentScrollY = window.scrollY;
    const now = Date.now();

    // Determine scroll direction
    if (currentScrollY > lastScrollY) {
      scrollDirection = "down";
    } else if (currentScrollY < lastScrollY) {
      scrollDirection = "up";
    }

    // Reset state change count if enough time has passed
    if (now - lastStateChangeTime > 1000) {
      stateChangeCount = 0;
    }

    // Prevent rapid state changes (more than 3 in 1 second indicates flutter)
    if (stateChangeCount >= 3) {
      ticking = false;
      lastScrollY = currentScrollY;
      return;
    }

    let shouldChangeState = false;

    // Only update state if we cross the appropriate threshold AND are scrolling in the right direction
    if (lastState && currentScrollY < expandThreshold && scrollDirection === "up") {
      shouldChangeState = true;
      setIsScrolled(false);
      lastState = false;
    } else if (!lastState && currentScrollY > condensedThreshold && scrollDirection === "down") {
      shouldChangeState = true;
      setIsScrolled(true);
      lastState = true;
    }

    if (shouldChangeState) {
      stateChangeCount++;
      lastStateChangeTime = now;
    }

    lastScrollY = currentScrollY;
    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollState);
      ticking = true;
    }
  };

  window.addEventListener("scroll", onScroll);

  return () => window.removeEventListener("scroll", onScroll);
}

export default function TopActionBar({
  user,
  logout,
  interior,
  actions,
  hasSecondaryNav: _hasSecondaryNav,
  primaryNav,
  primaryNavGoNav,
  keyboardShortcutEntries: keyboardShortcutEntriesProp = []
}: TopActionBarProps) {
  const { picture, name, email, department } = user;
  const flags = useFlags();
  const { assumedUser, stopAssumingUser } = useAssumeUser();
  const [isScrolled, setIsScrolled] = useState(false);
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);
  const toggleKeyboardShortcuts = useCallback(() => {
    setKeyboardShortcutsOpen((open) => !open);
  }, []);
  useKeyboardShortcutsHelpShortcut({
    onToggle: toggleKeyboardShortcuts,
    helpDialogOpen: keyboardShortcutsOpen
  });
  const { width } = useWindowSize();
  const isLargeScreen = isLargeDesktop(width);
  const { isCompactLayout: isMobileDisplay } = useIsCompactLayout();

  const { capabilities } = useCapabilities();
  const isUniversityStaff = useHasRoles([ROLE.UNIVERSITY_STAFF]);
  const canAssumeUser = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.assumeUser });
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPrimarySection, primarySectionId } = usePrimarySection();
  const openTaskCount = useOpenTaskCount();
  const canViewTasks = useCanViewTasksBool();
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewFacilities = useHasCapabilityBool({ domain: DOMAIN.FACILITIES, permission: PERMISSION.viewFacilities });
  const canViewMcpPlayground = canAccessMcpPlayground(canViewPeople, canViewFacilities);

  const keyboardShortcutEntries = useMemo(() => {
    if (keyboardShortcutEntriesProp.length > 0) return keyboardShortcutEntriesProp;

    return buildKeyboardShortcutEntries({
      showGoNav: Boolean(primaryNavGoNav),
      addMenuItems: [],
      canViewMcpPlayground
    });
  }, [canViewMcpPlayground, keyboardShortcutEntriesProp, primaryNavGoNav]);

  const openMcpPlayground = useCallback(() => {
    navigate("/dev/mcp-playground" as Path);
  }, [navigate]);

  useEffect(() => getScrollDirection(setIsScrolled), []);

  // Use compact styling only when scrolled
  const shouldUseCompactStyling = isScrolled;

  const sectionsLink =
    hasPrimarySection && primarySectionId ? (`/sections/${primarySectionId}/jobs/list` as Path) : ("/sections" as Path);

  const organizationLinks = isUniversityStaff
    ? []
    : defaultNavLinks(flags)
        .Organization.filter(
          (navLink) => navLink.domain === DOMAIN.EVERYONE || capabilities.domains.includes(navLink.domain)
        )
        .map((link) =>
          link.label === "Sections"
            ? {
                ...link,
                link: { ...link.link, to: sectionsLink } as typeof link.link
              }
            : link
        );

  const manageLinks = isUniversityStaff
    ? []
    : defaultNavLinks(flags).Manage.filter(
        (navLink) => navLink.domain === DOMAIN.EVERYONE || capabilities.domains.includes(navLink.domain)
      );

  const desktopNavLinks = [...organizationLinks, ...manageLinks];
  const fallbackVisibleCount = isLargeScreen ? desktopNavLinks.length : organizationLinks.length;
  const { containerRef, measureRef, visibleCount } = useDesktopNavOverflow({
    itemCount: desktopNavLinks.length,
    fallbackVisibleCount,
    measureKey: `${desktopNavLinks.map((link) => link.label).join("|")}:${shouldUseCompactStyling}`
  });
  const visibleDesktopNavLinks = desktopNavLinks.slice(0, visibleCount);
  const overflowDesktopNavLinks = desktopNavLinks.slice(visibleCount);
  const overflowMenuItems: MenuItem[] = overflowDesktopNavLinks.map((link) => ({
    label: link.label,
    onClick: () => navigate(link.link.to as Path)
  }));
  const isOverflowMenuActive = overflowDesktopNavLinks.some((link) =>
    isDesktopNavLinkActive(link.label, link.link.to as string, location.pathname)
  );

  const hasOpenTasks = openTaskCount != null && openTaskCount > 0;
  const currentUrl = `${location.pathname}${location.search}${location.hash}`;
  const assumeUserUrl = `/assume-user?return_to=${encodeURIComponent(currentUrl)}` as Path;
  const stopAssumingUserAndReturn = () => {
    const returnUrl = getInternalReturnUrl(assumedUser?.returnUrl);
    stopAssumingUser();
    navigate(returnUrl as Path);
  };

  const userMenuItems: MenuListEntry[] = [
    {
      label: name || "Unknown User",
      supportingText: [email, department].filter(Boolean).join("\n"),
      nonInteractive: true,
      itemClassName: MENU_PANEL_WIDTH_WIDE,
      useDivider: true
    },
    ...(canViewTasks
      ? [
          {
            label: "My Tasks",
            iconName: "inventory" as const,
            onClick: () => navigate("/tasks" as Path),
            dataTestId: "view-tasks-menu-item",
            showBadge: hasOpenTasks,
            useDivider: canViewMcpPlayground
          }
        ]
      : []),
    ...(canViewMcpPlayground
      ? [
          {
            label: "Chat with Member 360",
            iconName: "smart_toy" as const,
            onClick: openMcpPlayground,
            dataTestId: "mcp-playground-menu-item",
            shortcutKey: "/",
            shortcutShowModifier: false,
            useDivider: false
          }
        ]
      : []),
    ...(assumedUser
      ? [
          {
            label: "Stop Assuming User",
            iconName: "logout" as const,
            onClick: stopAssumingUserAndReturn,
            dataTestId: "stop-assuming-user-menu-item",
            useDivider: false
          }
        ]
      : canAssumeUser
        ? [
            {
              label: "Assume a User",
              iconName: "person" as const,
              onClick: () => navigate(assumeUserUrl),
              dataTestId: "assume-user-menu-item",
              useDivider: false
            }
          ]
        : []),
    {
      label: "Keyboard Shortcuts",
      iconName: "keyboard" as const,
      onClick: () => setKeyboardShortcutsOpen(true),
      dataTestId: "keyboard-shortcuts-menu-item",
      shortcutKey: "?",
      shortcutShowModifier: true,
      shortcutImplicitShift: true,
      useDivider: true
    },
    ...getBackendSourceMenuItems(),
    { label: "Logout", iconName: "logout" as const, onClick: logout }
  ];

  const bgColor = interior ? "md:bg-sys-brand-surface bg-sys-brand-surface-container" : "bg-sys-brand-surface";

  // Dynamic classes based on scroll state and screen size - only two variants: mobile vs desktop
  const useDesktopStyling = !isMobileDisplay && !shouldUseCompactStyling;

  const marginClasses = useDesktopStyling ? "sm:m-4" : "";

  let paddingClasses;
  if (isMobileDisplay) {
    paddingClasses = "px-3";
  } else if (shouldUseCompactStyling) {
    paddingClasses = "px-3 md:px-12 xl:px-16";
  } else {
    paddingClasses = "px-3 md:px-12 xl:px-20";
  }

  const borderRadiusClasses = useDesktopStyling ? "sm:rounded-md" : "";
  const shadowBorderClasses = useDesktopStyling ? "shadow-web-3" : "border-b border-sys-brand-outline-variant";

  const desktopGoNavProps = (label: string) =>
    primaryNavGoNav
      ? {
          showChordKeyCaps: primaryNavGoNav.goNavActive,
          modeActive: primaryNavGoNav.goNavActive,
          chordKey: getGoNavChordKey(label),
          dataTestId: `go-nav-${primaryNavItemSlug(label)}`
        }
      : undefined;

  return (
    <>
      <div
        className={`${marginClasses} ${paddingClasses} py-2 sticky top-0 z-[49] ${shadowBorderClasses} ${borderRadiusClasses} ${bgColor} transition-all duration-200 ease-in-out`}
        data-testid="top-action-bar"
      >
        <div className={`flex w-full min-w-0 items-center ${isMobileDisplay ? "min-w-0 gap-2" : ""}`}>
          {isMobileDisplay ? (
            <div className="lg:hidden z-drawer flex min-w-0 flex-1 items-center gap-2">
              <NavLogo className={`${COMPACT_GLOBAL_NAVBAR_LOGO_SIZE_CLASS} shrink-0`} />
              {primaryNav && !isUniversityStaff && (
                <>
                  <div className="h-6 w-px shrink-0 bg-sys-brand-outline-variant" aria-hidden="true" />
                  <div className="min-w-0">
                    <CompactPrimaryNavMenu
                      tabItems={primaryNav.tabItems}
                      domains={primaryNav.domains}
                      open={primaryNavGoNav?.menuOpen}
                      onOpenChange={primaryNavGoNav?.onMenuOpenChange}
                      showChordKeyCaps={primaryNavGoNav?.goNavActive}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="lg:hidden flex items-center z-drawer flex-shrink-0">
              <NavLogo />
            </div>
          )}

          <div className="hidden lg:flex min-w-0 flex-1 items-center">
            <NavLogo />
            <div className="relative mx-8 min-w-0 flex-1">
              <div
                className="pointer-events-none absolute left-0 top-0 h-0 overflow-hidden"
                aria-hidden="true"
                data-testid="desktop-nav-measure"
                ref={(node) => {
                  node?.toggleAttribute("inert", true);
                }}
              >
                <div ref={measureRef} className="flex w-max flex-nowrap items-center gap-2">
                  {desktopNavLinks.map((link) => (
                    <div key={link.label} className="shrink-0" data-desktop-nav-measure-item="">
                      <NavItem
                        label={link.label}
                        link={link.link}
                        iconName={link.icon}
                        hideIcon={shouldUseCompactStyling}
                        measureClone
                      />
                    </div>
                  ))}
                  <div className="shrink-0" data-desktop-nav-measure-more="">
                    <NavItem
                      label="More"
                      iconName="pending"
                      menuItems={[]}
                      hideIcon={shouldUseCompactStyling}
                      measureClone
                    />
                  </div>
                </div>
              </div>
              <div
                ref={containerRef}
                className={classNames(
                  "flex min-w-0 flex-1 flex-nowrap items-center gap-2",
                  primaryNavGoNav?.goNavActive && "relative"
                )}
                data-testid="desktop-nav-items"
                data-go-nav-chord-root
              >
                {primaryNavGoNav?.goNavActive && (
                  <GoNavSlidingGhost rootRef={containerRef} active={primaryNavGoNav.goNavActive} />
                )}
                {visibleDesktopNavLinks.map((link) => (
                  <div key={link.label} className="shrink-0">
                    <NavItem
                      label={link.label}
                      link={link.link}
                      iconName={link.icon}
                      active={isDesktopNavLinkActive(link.label, link.link.to as string, location.pathname)}
                      hideIcon={shouldUseCompactStyling}
                      goNav={desktopGoNavProps(link.label)}
                    />
                  </div>
                ))}
                {overflowMenuItems.length > 0 && (
                  <div className="shrink-0" data-testid="desktop-nav-more">
                    <NavItem
                      label="More"
                      iconName="pending"
                      menuItems={overflowMenuItems}
                      active={isOverflowMenuActive}
                      hideIcon={shouldUseCompactStyling}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className={`ml-auto flex shrink-0 items-center ${isMobileDisplay ? "gap-2 sm:gap-3" : "gap-3"}`}
            data-testid="top-action-bar-actions"
          >
            <div id="icons" className={`flex ${isMobileDisplay ? "shrink-0 items-center" : "gap-3"}`}>
              {actions}
            </div>
            <Menu
              yOffset={8}
              AnchorComponent={
                <div className={`relative ${isMobileDisplay ? "shrink-0" : ""}`} data-testid="user-profile-menu">
                  <Avatar
                    picture={picture}
                    size={isMobileDisplay ? "small" : "medium"}
                    className={isMobileDisplay ? COMPACT_GLOBAL_NAVBAR_AVATAR_SIZE_CLASS : undefined}
                  />
                  {canViewTasks && hasOpenTasks && (
                    <Badge
                      value={openTaskCount}
                      className="absolute -top-1 -right-1 pointer-events-none"
                      testId="avatar-task-badge"
                    />
                  )}
                </div>
              }
              menuItems={userMenuItems}
            />
          </div>
        </div>
      </div>
      <KeyboardShortcutsDialog
        open={keyboardShortcutsOpen}
        onOpenChange={setKeyboardShortcutsOpen}
        entries={keyboardShortcutEntries}
      />
    </>
  );
}
